import { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useLocation, useNavigate } from 'react-router-dom';

import { ApiManager } from '../../../managers';
import { CookieValues, ModelNameEnum } from '../../../types';
import { AlertContext } from '../../Alert';
import NavigationBarContext, { NavigationBarContextProps } from './NavigationBar.context';

type NavigationBarProviderProps = {
  children: ReactNode;
};

/**
 * Manages state and interactions for navigation bar
 *
 * See {@link NavigationBarContext} for a full list of state and methods available
 */
function NavigationBarProvider({ children }: NavigationBarProviderProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const { showAlert } = useContext(AlertContext);

  const [cookies] = useCookies<'token', CookieValues>(['token']);

  const [selectedNavItem, setSelectedNavItem] = useState<ModelNameEnum | null>(null);
  const [navData, setNavData] = useState<any[]>([]);
  const [, setIsLoading] = useState<boolean>(false);

  const navigationItemOnSelect: NavigationBarContextProps['navigationItemOnSelect'] = useCallback(
    (eventKey: ModelNameEnum | null, updatePath: boolean) => {
      // User wants to navigate to a screen outside the DataManager
      if (updatePath) {
        setSelectedNavItem(null);
        if (eventKey === null) {
          const isLoggedIn = !!cookies.token;
          navigate(isLoggedIn ? '/home' : '/');
        } else {
          navigate(eventKey);
        }
      } else {
        setSelectedNavItem(eventKey);
        // User is navigating to view table data for a model within the DataManager
        if (location.pathname !== 'home') {
          navigate('home');
        }
      }
    },
    [location.pathname, cookies.token, navigate],
  );

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const response = await ApiManager.SendRequest({
          route: 'component',
          action: 'side-nav',
          apiToken: cookies.token,
        });

        if (response?.error !== null) {
          showAlert({
            headerText: 'Uh-oh',
            detailText: response?.error ?? '',
            variant: 'danger',
            dismissText: 'okay',
          });
        } else {
          setNavData(response.results);
        }
      } catch (err: any) {
        showAlert({
          headerText: 'Uh-oh',
          detailText: `An error has occurred, please try again or contact support.\nError: ${err.emssage}`,
          variant: 'danger',
          dismissText: 'okay',
          err,
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (cookies.token) {
      loadData();
    }
  }, [cookies.token, showAlert]);

  const value = useMemo(
    () => ({
      selectedNavItem,
      navigationItemOnSelect,
      navData,
    }),
    [selectedNavItem, navigationItemOnSelect, navData],
  );

  return <NavigationBarContext.Provider value={value}>{children}</NavigationBarContext.Provider>;
}

NavigationBarProvider.Context = NavigationBarContext;

export default NavigationBarProvider;
